import { useState, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import axiosInstance from '../utils/axiosInstance';
import { Box, TextField, Button, Stack, Autocomplete } from '@mui/material';
import HowToReg from '@mui/icons-material/HowToReg';
import SupplierSelectModal from './SupplierSelectModal';
const PurchaseSupplierInfo = ({
    hasLimit,
    isEditMode = true,
    register,
    control,
    setValue,
    errors,
    fieldErrors = {},
    setFieldErrors,
    setSelectedSupplier,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [contactOptions, setContactOptions] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState([]);

    // 担当者オートコンプリート
    const supplierId = useWatch({ control, name: 'supplier_id' });
    useEffect(() => {
        if (!supplierId) {
            setContactOptions([]);
            return;
        }

        const fetchContacts = async () => {
            try {
                const res = await axiosInstance.get(`/supplier/${supplierId}`);

                const formatted = (res.data.contacts || []).map(
                    ({ name, email, phone, ...rest }) => ({
                        ...rest,
                        supplier_contact_name: name,
                        supplier_contact_email: email,
                        supplier_contact_phone: phone,
                    })
                );

                setContactOptions(formatted);

            } catch (err) {
                if (err.response?.status === 404) {
                    setValue('supplier_contact_id', null);
                    return;
                }
                console.error(err);
            }
        };

        fetchContacts();
    }, [supplierId]);


    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                const res = await axiosInstance.get('/list/payment-methods', { withCredentials: false });
                setPaymentOptions(res.data || []);
            } catch (err) {
                console.error(err);
            }
        };

        fetchPaymentMethods();
    }, []);

    const handleCustomerSelect = (supplier) => {
        setIsModalOpen(false);
        setValue('supplier_id', supplier.supplier_id);
        setValue('supplier_name', supplier.supplier_name);
        setValue('supplier_phone', supplier.supplier_phone);
        setValue('payment_terms', supplier.payment_terms);
        setValue('payment_method_id', supplier.payment_method_id);
        setValue('payment_method_name', supplier.payment_method_name);
        setValue('supplier_contact_id', null);
        setValue('supplier_contact_name', '');
        setValue('supplier_contact_phone', '');
        setValue('supplier_contact_email', '');
        setFieldErrors?.({ ...fieldErrors, supplier_name: '', supplier_contact_name: '' });
        setSelectedSupplier?.(supplier);
    };
    return (
        <>
            <Stack direction="row" spacing={1} mb={2} mt={1} alignItems="start">
                <TextField
                    label="会社名"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('supplier_name', {
                        required: '※会社名は必須です',
                        maxLength: {
                            value: 255,
                            message: '※255文字以内で入力してください'
                        },
                        onChange: () => {
                            setValue('supplier_id', null, { shouldDirty: true });
                            setValue('supplier_contact_id', null);
                            setValue('shipping_address_id', null);
                        }
                    })}
                    error={!!errors.supplier_name}
                    helperText={errors.supplier_name?.message}
                />
                <Button
                    variant="outlined"
                    sx={{ minWidth: 130 }}
                    startIcon={<HowToReg />}
                    onClick={() => setIsModalOpen(true)}
                    disabled={!hasLimit('max_suppliers') || !isEditMode}
                >
                    仕入先を選択
                </Button>

            </Stack >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', rowGap: 2, columnGap: 1, }}>
                <TextField
                    label="電話番号"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 20 }}
                    {...register('supplier_phone', {
                        pattern: {
                            value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                            message: '※数字とハイフンのみ使用できます'
                        },
                        maxLength: {
                            value: 20,
                            message: '※20文字以内で入力してください'
                        }
                    })}
                    error={!!errors.supplier_phone}
                    helperText={errors.supplier_phone?.message}
                />

                <Controller
                    name="supplier_contact_name"
                    control={control}
                    rules={{
                        maxLength: {
                            value: 100,
                            message: '※100文字以内で入力してください'
                        }
                    }}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            {...field}
                            freeSolo
                            openOnFocus
                            options={contactOptions}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.supplier_contact_name
                            }
                            value={
                                contactOptions.find(c => c.supplier_contact_name === field.value) || field.value || ''
                            }
                            onChange={(e, newValue) => {
                                const value =
                                    typeof newValue === 'string'
                                        ? newValue
                                        : newValue?.supplier_contact_name || '';

                                field.onChange(value);

                                if (typeof newValue === 'string') {
                                    setValue('supplier_contact_id', null);
                                    setValue('supplier_contact_phone', '');
                                    setValue('supplier_contact_email', '');
                                } else if (newValue) {
                                    setValue('supplier_contact_id', newValue.supplier_contact_id);
                                    setValue('supplier_contact_name', newValue.supplier_contact_name ?? '');
                                    setValue('supplier_contact_phone', newValue.supplier_contact_phone ?? '');
                                    setValue('supplier_contact_email', newValue.supplier_contact_email ?? '');
                                }
                            }}
                            onInputChange={(e, value) => {
                                field.onChange(value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="担当者名"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    inputProps={{
                                        ...params.inputProps,
                                        maxLength: 100
                                    }}
                                />
                            )}
                            disabled={!isEditMode}
                        />
                    )}
                />

                <TextField
                    label="担当者TEL"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 20 }}
                    {...register('supplier_contact_phone', {
                        pattern: {
                            value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                            message: '※電話番号形式が正しくありません'
                        },
                        maxLength: {
                            value: 20,
                            message: '※20文字以内で入力してください'
                        }
                    })}
                    error={!!errors.supplier_contact_phone}
                    helperText={errors.supplier_contact_phone?.message}
                />

                <TextField
                    label="担当者EMAIL"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('supplier_contact_email', {
                        required: '※担当者EMAILは必須です',
                        maxLength: {
                            value: 255,
                            message: '※EMAILは255文字以内で入力してください'
                        },
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: '※正しいメールアドレス形式で入力してください'
                        }
                    })}
                    error={!!errors.supplier_contact_email}
                    helperText={errors.supplier_contact_email?.message}
                />
                {/*
                <TextField
                    label="支払い条件"
                    disabled={!isEditMode}
                    inputProps={{ maxLength: 255 }}
                    {...register('payment_terms', {
                        maxLength: {
                            value: 255,
                            message: '※支払い条件は255文字以内で入力してください'
                        }
                    })}
                    error={!!errors.payment_terms}
                    helperText={errors.payment_terms?.message}
                />
                <Controller
                    name="payment_method_name"
                    control={control}
                    rules={{
                        maxLength: {
                            value: 100,
                            message: '※100文字以内で入力してください'
                        }
                    }}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            {...field}
                            freeSolo
                            options={paymentOptions}
                            getOptionLabel={(option) => option.payment_method_name || option}
                            value={
                                paymentOptions.find(
                                    p => p.payment_method_name === field.value
                                ) || field.value || null
                            }
                            onChange={(e, newValue) => {
                                const value = typeof newValue === 'string'
                                    ? newValue
                                    : newValue?.payment_method_name ?? '';

                                field.onChange(value);

                                if (typeof newValue === 'string') {
                                    // 自由入力の場合はIDはクリア
                                    setValue('payment_method_id', null);
                                } else if (newValue) {
                                    // 候補選択時はIDもセット
                                    setValue('payment_method_id', newValue.payment_method_id ?? null);

                                }
                            }}
                            onInputChange={(e, value, reason) => {
                                if (reason === 'input') {
                                    field.onChange(value);
                                    setValue('payment_method_id', null);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="支払方法"
                                    inputProps={{
                                        ...params.inputProps,
                                        maxLength: 100
                                    }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                            disabled={!isEditMode}
                        />
                    )}
                />
                 */}
            </Box >
            <SupplierSelectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelectSupplier={handleCustomerSelect}
            />
        </>
    );
};
export default PurchaseSupplierInfo;